import {createAction, createAsyncThunk} from '@reduxjs/toolkit'

import {isApiError} from '@shared/helpers/api'
import {some} from '@shared/helpers/collection'
import {delayTimeout} from '@shared/helpers/function'
import {isEmpty, isArray} from '@shared/helpers/lang'
import {mapValues} from '@shared/helpers/object'

import {initOrderSheetByStorage} from '@web/actions/orderSheetInfo'
import {getPayLaterInfo} from '@web/actions/payLater'
import {setMaximumRechargePoint, updatePaymentMethod} from '@web/actions/paymentMethod'
import {enablePayLaterPointByAllPoint, updatePointPayment, usingAllNaverPoint} from '@web/actions/pointPayment'
import {updateCouponDiscount} from '@web/actions/productCoupon'
import {
    APIVersionType,
    PaymentsCardCodeType,
    TaxType,
    EntryType,
    PayMethodType,
    OrderEntry,
    ErrorType,
    OrderSheetPageSetType,
    UserAuthStatusType,
    UserAuthStatus,
} from '@web/core/constants'
import {ROUTER_ORDER_CHECK_OUT} from '@web/core/routes'
import {NAVER_CAFE_CLOSE} from '@web/core/urls'
import {isSafeUrl, convertEasyBooingApiResponseChannelType} from '@web/helpers/path'
import {isCafeApp, isMobileDisplay} from '@web/helpers/userAgent'
import {orderInterrupted} from '@web/middlewares/orderInterrupted'
import {paymentMethodInfoSelector, rechargePaymentInfoSelector} from '@web/selectors/paymentMethodInfo'
import {naverPayPointInfoSelector, payLaterInfoSelector} from '@web/selectors/pointPaymentInfo'
import {discountInfoSelector} from '@web/selectors/productInfo'
import {addNaverId} from '@web/slices/abTest/slice'
import {
    SaleChannelInfoType,
    orderEntryInfoSelector,
    commonSelector,
    pageSetInfoSelector,
} from '@web/slices/common/slice'
import {recalculateAction} from '@web/slices/orderTotal/slice'
import {payLaterSelector} from '@web/slices/payLater/slice'
import {getAvailablePayMethodTypes} from '@web/slices/paymentMethod/slice'
import {naverPayPointSelector} from '@web/slices/pointPayment/slice'

import easyBookingApis from '../apis/easyBooking'
import {FetchOrderSheetError} from '../apis/interface/response'
import {FulfilledException} from '../constants'
import {getDeliveryException} from '../utils/delivery'
import {filterCardCode} from '../utils/payments'

import type {
    AdditionalOrderOptions,
    Agreement,
    BillingProduct,
    CategoryProduct,
    Coupons,
    DeliveryAddressBook,
    DeliveryFee,
    MerchantsByMerchantNo,
    NaverMembership,
    NaverpayPoint,
    OrderSheetProductId,
    PayLater,
    Product,
    ProductItem,
    Purchaser,
    TimeStamp,
    PayMethod as ResPayMethod,
    Banner,
    PurchaserCommissionsByPayMethodType,
    CupDeposit,
    FamilyPayMember,
    OverseaCardEasyPay,
    CardCompany,
    CardEasyPay,
    CreditCardBillDiscountView,
    PayMethodStatement,
    BankEasyPayView,
    DefaultPayment,
    BillingPayMethodsInfo,
} from '../apis/interface/response/interface'
import type {ThunkApi} from '../interfaces'

/** 안전결제 수수료 재계산 action */
export const updateSafetyPurchaseAmountAction = createAction<PayMethodType>('updateSafetyPurchaseAmountAction')

export const NO_OP = {
    type: 'NO_OP',
} as const

export type Products = (Product &
    BillingProduct &
    CategoryProduct & {
        items: ProductItem[]
        merchantProductId: string
        orderSheetProductId: OrderSheetProductId
        overseasDelivery: boolean
        salePrice: number
        taxType: TaxType
        orderAmount: number
        cupDeposit: Maybe<CupDeposit>
    })[]

// 주문서 조회 후 반환되는 payload
export type InitOrderSheetByAPIPayloadType = {
    isMobileDisplay: boolean
    backUrl: string
    coupon: Coupons
    cultureBenefit: boolean
    delivery: DeliveryFee
    deliveryAddressBook: Maybe<DeliveryAddressBook>
    deliveryNothingOrder: boolean
    merchantsByMerchantNo: MerchantsByMerchantNo
    naverMembership: NaverMembership
    naverpayPoint: Maybe<NaverpayPoint>
    purchaser: Purchaser
    verifyServerClientData: string
    /** prepare 요청시 결제정보 변조확인을 위한 key */
    verificationKey: string
    orderSheetId: string
    payMethod: ResPayMethod & {
        /**
         * 특정 카드에 혜택을 주기위한 카드 목록
         * - 카드혜택목록 존재 시 카드 결제만 가능
         * - 쿠폰 및 포인트 사용 불가
         * - Spec 변경시 payMethodAvailableSelector 확인 필요
         **/
        partnershipCardCodes: Maybe<PaymentsCardCodeType[]>
        /**
         * 예약 주문서의 경우 해외 간편카드 목록
         */
        overseasCardEasyPays: OverseaCardEasyPay[]
    }
    products: Products
    additionalOrderOptions?: Maybe<AdditionalOrderOptions>
    agreement?: Agreement
    banner: Banner
    payLater?: PayLater
    productApiType?: APIVersionType
    registeredDateTime?: TimeStamp
    purchaserCommissionsByPayMethodType?: Maybe<PurchaserCommissionsByPayMethodType>
    saleChannelInfo: SaleChannelInfoType
    isOverseasDelivery: boolean
    availablePayMethodTypes: PayMethodType[]
    familyPay?: Maybe<FamilyPayMember>
    language?: 'ko' | 'en' | 'ja' | 'zh'
    userAuthStatus?: UserAuthStatusType
    fleaMarketCommission?: {
        /** 결제 수수료 */
        purchaserCommissionAmount: number
        /** 배송비 수수료 */
        deliveryFeeCommissionAmount: number
        /** 수수료 정책번호 */
        policyNo: string
    }
    entry: Maybe<EntryType>
}

export const initOrderSheetByAPI = createAsyncThunk<
    InitOrderSheetByAPIPayloadType,
    string,
    ThunkApi<FetchOrderSheetError>
>('initOrderSheetByAPI', async (orderSheetId, {getState, rejectWithValue, dispatch}) => {
    const saleChannelInfo = orderEntryInfoSelector(getState())
    const {entry} = commonSelector(getState())

    try {
        const {payMethod, products, delivery, backUrl, saleChannelType, ...response} =
            await easyBookingApis.fetchOrderSheetInfo(orderSheetId)

        /**
         * 예로블카 > api응답값의 saleChannel 정보와 접근한 url의 saleChannel 정보가 일치 하지 않을때
         * ordersheetId와 맵핑된 최초의 주문서 url로 redirect (최초의 saleChannel 포함된 url)
         */
        if (saleChannelType !== 'FLEAMARKET') {
            const redirectUrl = (function () {
                switch (convertEasyBooingApiResponseChannelType(saleChannelType)) {
                    case OrderEntry.EASY_BOOKING:
                        return ROUTER_ORDER_CHECK_OUT.EASY_BOOKING
                    case OrderEntry.LOCAL_PAY:
                        return ROUTER_ORDER_CHECK_OUT.LOCAL_PAY
                    case OrderEntry.NAVER_BLOG:
                        return ROUTER_ORDER_CHECK_OUT.BLOG
                    default:
                        return ''
                }
            })()
            dispatch(
                orderInterrupted([
                    redirectUrl
                        ? {type: 'REDIRECT', url: `${redirectUrl}/${orderSheetId}`}
                        : {type: 'BACK', shouldClose: true},
                ]),
            )
        }

        // 배송 단위 식별 키 (deliveryBundleKey 존재 하지 않으면, orderSheetProductId 반환)
        // 예블카에서는 항상 하나의 묶음 배송으로만 표현되고 있습니다.
        // const deliveryGroupKey = Object.keys(response.delivery.deliveryGroupsByKey)[0]
        const result: InitOrderSheetByAPIPayloadType = {
            orderSheetId,
            ...response,
            // 네이버 카페앱은 prevUrl, closeUrl 시 닫기 처리 합니다.
            backUrl: isCafeApp
                ? NAVER_CAFE_CLOSE
                : // @see https://oss.fin.navercorp.com/Security-Privacy/Security/issues/1950#issuecomment-2012435
                  isSafeUrl(backUrl)
                  ? backUrl
                  : '',
            isMobileDisplay,
            delivery: {
                remoteAdditionalDeliveryFeeFailed: null,
                remoteAdditionalDeliveryFeeFailureMessage: null,
                ...delivery,
                deliveryGroupsByKey: mapValues((deliveryGroups) => {
                    const {deliveryPolicy} = deliveryGroups
                    return {
                        ...deliveryGroups,
                        deliveryPolicy: {
                            additionalFeeMessage: null,
                            repeatQuantity: null,
                            secondBaseQuantity: null,
                            secondExtraFee: null,
                            thirdBaseQuantity: null,
                            thirdExtraFee: null,
                            ...deliveryGroups.deliveryPolicy,
                            baseFee: deliveryPolicy.baseFee ?? 0,
                        },
                    }
                }, delivery.deliveryGroupsByKey),
            },
            payMethod: {
                bankEasyPayResultType: 'SUCCESS',
                usePayLaterAsDefault: false,
                pendingPaymentInfo: null,
                usePayLaterAll: false,
                ...payMethod,
                availablePayMethodTypes: payMethod.availablePayMethodTypes as PayMethodType[],
                creditCardBillDiscounts: payMethod.creditCardBillDiscounts as CreditCardBillDiscountView[],
                payMethodStatement: payMethod.payMethodStatement as PayMethodStatement,
                recentPayMethodType: payMethod.recentPayMethodType as Maybe<PayMethodType>,
                recentChargedAccountFirstBankEasyPays:
                    payMethod.recentChargedAccountFirstBankEasyPays as BankEasyPayView[],
                recentUsedFirstBankEasyPays: payMethod.recentUsedFirstBankEasyPays as BankEasyPayView[],
                billingPayMethods: payMethod.billingPayMethods as BillingPayMethodsInfo,
                defaultPayment: payMethod.defaultPayment as Maybe<DefaultPayment>,
                cardCompanies: filterCardCode({
                    prop: 'code',
                    cards: payMethod.cardCompanies,
                    availableAll: isEmpty(payMethod.easybookingAvailableCards),
                    availableCardCodes: payMethod.easybookingAvailableCards,
                }) as CardCompany[],
                cardEasyPays: filterCardCode({
                    prop: 'cardCompanyCode',
                    cards: payMethod.cardEasyPays,
                    availableAll: isEmpty(payMethod.easybookingAvailableCards),
                    availableCardCodes: payMethod.easybookingAvailableCards,
                }) as CardEasyPay[],
                partnershipCardCodes: payMethod.easybookingAvailableCards as PaymentsCardCodeType[],
                overseasCardEasyPays: payMethod.overseasCardEasyPays as OverseaCardEasyPay[],
            },
            products: products.map((product) => ({
                categoryId: null,
                wholeCategoryId: null,
                detailCategoryId: null,
                largeCategoryId: null,
                middleCategoryId: null,
                smallCategoryId: null,
                taxType: 'TAX_FREE',
                gift: null,
                ...product,
            })),
            deliveryAddressBook: (response.deliveryAddressBook as DeliveryAddressBook) ?? null,
            saleChannelInfo,
            isOverseasDelivery:
                some(({personalCustomsCodeExposed}) => personalCustomsCodeExposed, response.merchantsByMerchantNo) ||
                products.some(({overseasDelivery}) => overseasDelivery),
            availablePayMethodTypes: getAvailablePayMethodTypes({
                availablePayMethodTypes: payMethod.availablePayMethodTypes as PayMethodType[],
                pointRestrictPolicy: 'EACH',
            }),
            language: response.foreignerOrder?.languageCode,
            userAuthStatus: response?.userAuthStatus ?? UserAuthStatus.CERTIFICATE,
            coupon: response.coupon as Coupons,
            naverMembership: response.naverMembership as NaverMembership,
            naverpayPoint: response.naverpayPoint as Maybe<NaverpayPoint>,
            purchaser: response.purchaser as Purchaser,
            agreement: response.agreement as Agreement,
            banner: response.banner as Banner,
            entry,
        }
        return result
    } catch (e) {
        if (isApiError(e)) {
            if (e.response?.status === 504) {
                return rejectWithValue({
                    code: ErrorType.ETIMEDOUT_FAILED,
                    fallbackMessage: '일시적인 오류가 발생하였습니다. 잠시 후 다시 시도해 주세요.',
                    status: e.response.status,
                    userAuthStatus: e.response.data?.userAuthStatus ?? UserAuthStatus.CERTIFICATE,
                } as FetchOrderSheetError)
            }
            return rejectWithValue(e.response?.data?.result)
        }
        throw e
    }
})

export const initOrderSheet = createAsyncThunk<
    {
        isApiSuccess: boolean
        isRestore: boolean
        isZipCodeChanged: boolean
        entry: Maybe<EntryType>
        exception?: FulfilledException
    },
    string,
    ThunkApi
>('initOrderSheet', async (seqID, {getState, dispatch}) => {
    const state = getState()
    const apiResult = await dispatch(initOrderSheetByAPI(seqID)).unwrap()
    const {orderSheetPageSet} = pageSetInfoSelector(state)
    let isRestore = false
    let isZipCodeChanged = false
    const isApiSuccess = !!apiResult
    if (isApiSuccess) {
        /** 총 할인 금액 */
        const discountInfo = discountInfoSelector(getState())
        dispatch(
            recalculateAction({
                totalDiscountAmount: discountInfo.totalDiscountAmount,
                productDiscountAmount: discountInfo.totalProductDiscountAmount,
                deliveryDiscountAmount: discountInfo.totalDeliveryDiscountAmount,
            }),
        )

        // 데이터 반영후 후불결제 최대화 계산
        await dispatch(getPayLaterInfo())

        /** [TODO] 정책서에 C3는 후불결제 미사용 주문서로 명시되어있던데 확인 필요 */
        if (payLaterSelector(getState()).enabled) {
            await dispatch(enablePayLaterPointByAllPoint())
        } else if (naverPayPointSelector(getState()).usePointAll) {
            // 데이터 반영후 포인트 최대화 계산
            await dispatch(usingAllNaverPoint())
        } else if (rechargePaymentInfoSelector(getState()).isNeedRecharging) {
            // 데이터 반영후 충전포인트 최대화 계산
            await dispatch(setMaximumRechargePoint())
        }
        // 화면이 노출되고 confirm 가 노출되어야 합니다.
        await delayTimeout(0)

        let isCouponRestore = false
        await dispatch(initOrderSheetByStorage({orderSheetId: seqID, isCouponFailed: apiResult.coupon.fallback}))
            .unwrap()
            .then(({data: restoreData, differences, isRestore: shouldRestore}) => {
                isRestore = shouldRestore
                if (restoreData && differences && isArray(differences)) {
                    isZipCodeChanged = differences.includes('DELIVERY')
                    isCouponRestore = differences.includes('COUPON')
                }
            })
            .catch((reject) => {
                isZipCodeChanged = reject && reject.case === 'FIRST_DELIVERY_PASS'
                isCouponRestore = false
            })
        if (isCouponRestore) {
            /** 할인금액 api 조회하여 복원 */
            await dispatch(
                updateCouponDiscount({
                    pageSet: orderSheetPageSet as Extract<
                        OrderSheetPageSetType,
                        'MALL_CHECKOUT' | 'EASY_BOOK_CHECKOUT'
                    >,
                }),
            )
            const {totalDiscountAmount, totalProductDiscountAmount, totalDeliveryDiscountAmount} =
                discountInfoSelector(getState())
            // 복원으로 선택된 쿠폰 기준으로 할인 금액 재반영
            dispatch(
                recalculateAction({
                    totalDiscountAmount,
                    productDiscountAmount: totalProductDiscountAmount,
                    deliveryDiscountAmount: totalDeliveryDiscountAmount,
                }),
            )
        }

        // 포인트 사용시 업데이트 필요 여부
        if (naverPayPointInfoSelector(getState()).isNeedUpdate || payLaterInfoSelector(getState()).isNeedUpdate) {
            /** [TODO] 정책서에 C3는 후불결제 미사용 주문서로 명시되어있던데 확인 필요 */
            await dispatch(updatePointPayment())
        } else if (rechargePaymentInfoSelector(getState()).isNeedRecharging) {
            // 충전결제 복원시 사용이 0원 이라면, 최대 적용으로 변경 합니다.
            await dispatch(setMaximumRechargePoint())
        }
        // 복원후 복합결제 상황이라면 결제수단 선택
        if (paymentMethodInfoSelector(getState()).isNeedSelectedPayMethod) {
            await dispatch(updatePaymentMethod())
        }
    }

    const {entry} = commonSelector(getState())

    dispatch(addNaverId(apiResult.purchaser.naverId))

    return {
        isApiSuccess,
        isRestore,
        isZipCodeChanged,
        entry,
        ...(apiResult?.delivery?.remoteAdditionalDeliveryFeeFailed && !isRestore
            ? {
                  // api 반영되고 풀필드 되었을때,
                  exception: getDeliveryException(apiResult.delivery),
              }
            : {}),
    }
})
